import { useState, useEffect } from 'react';
import { 
    collection, doc, addDoc, updateDoc, deleteDoc, 
    onSnapshot, Timestamp, setDoc, arrayUnion, query, where,
    orderBy, limit // Added orderBy and limit for log efficiency
} from 'firebase/firestore';
import { db } from '../services/firebaseConfig';

const APP_ID = 'worldgrad-crm';

/**
 * useCRM Hook
 * Central data synchronization with Role-Based Access Control (RBAC).
 */
export function useCRM(user) {
    const [students, setStudents] = useState([]);
    const [applicants, setApplicants] = useState([]);
    const [applications, setApplications] = useState([]);
    const [users, setUsers] = useState([]);
    /* NEW: State for Bot Logs */
    const [botLogs, setBotLogs] = useState([]); 
    const [settings, setSettings] = useState({ 
        twgPrograms: [], 
        universities: [], 
        sourceAgents: [], 
        countries: [],
        documentTemplates: {} 
    });
    const [loading, setLoading] = useState(true);

    // 1. Core Metadata Sync (Users and Global Settings)
    useEffect(() => {
        if (!user) return;

        const unsubUsers = onSnapshot(
            collection(db, 'artifacts', APP_ID, 'public', 'data', 'users'), 
            snap => {
                const usersList = snap.docs.map(d => ({ ...d.data(), id: d.id }));
                setUsers(usersList);
            }
        );

        const unsubSettings = onSnapshot(
            doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'global'),
            snap => { if (snap.exists()) setSettings(snap.data()); }
        );

        return () => { 
            unsubUsers(); 
            unsubSettings(); 
        };
    }, [user]);

    // 2. Data Listener Sync (Restricted by Role/Agency)
    useEffect(() => {
        if (!user || users.length === 0) return;

        const currentUserData = users.find(u => String(u.uid) === String(user.uid));
        if (!currentUserData) return;

        const isAdmin = currentUserData?.role === 'Admin';
        const isAppsTeam = currentUserData?.role === 'TWG Apps and Visa';
        const isB2B = currentUserData?.role === 'TWG B2B User';
        const isAgentMaster = currentUserData?.role === 'TWG Agent Master';
        const myAgentName = currentUserData?.agentAssociation || '';

        // --- Students ---
        const studentsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'students');
        let qStudents;
        if (isAdmin || isAppsTeam || isB2B) {
            qStudents = query(studentsRef);
        } else if (isAgentMaster) {
            qStudents = query(studentsRef, where('sourceAgent', '==', myAgentName));
        } else {
            qStudents = query(studentsRef, where('agentId', '==', user.uid));
        }
        const unsubStudents = onSnapshot(qStudents, snap => 
            setStudents(snap.docs.map(d => ({ ...d.data(), id: d.id })))
        );

        // --- Applicants ---
        const applicantsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'applicants');
        let qApplicants;
        if (isAdmin || isAppsTeam) {
            qApplicants = query(applicantsRef);
        } else if (isAgentMaster) {
            qApplicants = query(applicantsRef, where('agentName', '==', myAgentName));
        } else {
            qApplicants = query(applicantsRef, where('agentId', '==', user.uid));
        }
        const unsubApplicants = onSnapshot(qApplicants, snap => 
            setApplicants(snap.docs.map(d => ({ ...d.data(), id: d.id })))
        );

        // --- Applications ---
        const appsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'applications');
        let qApps;
        if (isAdmin || isAppsTeam || isB2B) {
            qApps = query(appsRef);
        } else if (isAgentMaster) {
            qApps = query(appsRef, where('partnerName', '==', myAgentName));
        } else {
            qApps = query(appsRef, where('agentId', '==', user.uid));
        }
        const unsubApps = onSnapshot(qApps, snap => {
            setApplications(snap.docs.map(d => ({ ...d.data(), id: d.id })));
            setLoading(false);
        }, err => {
            console.error("Firestore sync error:", err);
            setLoading(false);
        });

        /* --- NEW: Bot Intelligence Logs (Admin Only) --- */
        let unsubLogs = () => {};
        if (isAdmin) {
            const logsRef = collection(db, 'artifacts', APP_ID, 'public', 'data', 'bot_logs');
            const qLogs = query(logsRef, orderBy('timestamp', 'desc'), limit(200));
            unsubLogs = onSnapshot(qLogs, snap => {
                setBotLogs(snap.docs.map(d => ({ ...d.data(), id: d.id })));
            });
        }

        return () => {
            unsubStudents();
            unsubApplicants();
            unsubApps();
            unsubLogs(); // Clean up logs listener
        };
    }, [user, users]);

    const actions = {
        updateSettings: async (newSettings) => {
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'global');
            return setDoc(ref, newSettings);
        },
        saveApplicant: async (data, id = null) => {
            const payload = { ...data, updatedAt: Timestamp.now() };
            if (id) {
                const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'applicants', String(id));
                return await setDoc(ref, payload, { merge: true });
            } else {
                const ref = collection(db, 'artifacts', APP_ID, 'public', 'data', 'applicants');
                return await addDoc(ref, {
                    ...payload,
                    createdAt: Timestamp.now(),
                    agentId: user.uid,
                    createdBy: user.uid
                });
            }
        },
        deleteApplicant: async (id) => {
            if (!id) throw new Error("Invalid ID provided for deletion");
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'applicants', String(id));
            return deleteDoc(ref);
        },
        saveApplication: async (appData) => {
            if (!appData.id) throw new Error("Application ID missing");
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'applications', appData.id);
            return setDoc(ref, {
                ...appData,
                agentId: user.uid,
                updatedAt: Timestamp.now()
            }, { merge: true });
        },
        updateApplication: async (appId, data) => {
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'applications', appId);
            return setDoc(ref, { ...data, updatedAt: Timestamp.now() }, { merge: true });
        },
        saveStudent: async (data, id = null) => {
            const payload = { ...data, updatedAt: Timestamp.now() };
            if (id) {
                const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'students', String(id));
                return updateDoc(ref, payload);
            } else {
                const ref = collection(db, 'artifacts', APP_ID, 'public', 'data', 'students');
                return addDoc(ref, {
                    ...payload, 
                    documentChecklist: {},
                    timeline: [{
                        type: 'status',
                        content: 'Student Record Created',
                        timestamp: new Date().toISOString(),
                        user: user.displayName || 'System'
                    }],
                    createdAt: Timestamp.now(),
                    agentId: user.uid,
                    createdBy: user.uid
                });
            }
        },
        updateStudentDocuments: async (studentId, checklistData) => {
            if (!studentId) return;
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'students', String(studentId));
            return updateDoc(ref, {
                documentChecklist: checklistData,
                updatedAt: Timestamp.now()
            });
        },
        deleteStudent: async (id) => {
            if (!id) return;
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'students', String(id));
            return deleteDoc(ref);
        },
        addTimelineEvent: async (studentId, event) => {
            if (!studentId) return;
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'students', String(studentId));
            return updateDoc(ref, {
                timeline: arrayUnion(event),
                updatedAt: Timestamp.now()
            });
        },
        updateUserRole: async (docId, newRole) => {
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'users', docId);
            return updateDoc(ref, { role: newRole });
        },
        updateUserStatus: async (docId, newStatus) => {
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'users', docId);
            return updateDoc(ref, { status: newStatus });
        },
        updateUser: async (docId, data) => {
            const ref = doc(db, 'artifacts', APP_ID, 'public', 'data', 'users', docId);
            return updateDoc(ref, data);
        },
    };

    /* MODIFIED: Included botLogs in the return object */
    return { students, applicants, applications, users, botLogs, settings, actions, loading };
}